import { FC, ReactElement } from 'react'
import { useToggle } from 'react-use'

import { hasPermissionToEditUser } from '../../../helpers'
import { LoadingSpinner } from '../../components'
import {
    IconButton,
    IconNavLink,
    ModalContent,
    PacoLogsModal,
    PacoModal,
    UserDetailCards,
} from '../../compositions'
import {
    ConnectedDeactivateUserForm,
    ConnectedDeleteUserForm,
    ConnectedEditUserForm,
    ConnectedLegacyUserDetail,
    ConnectedReactivateUserForm,
} from '../../connectors'
import { EmploymentType } from '../../entities/EmploymentType/EmploymentType'
import { EventLog } from '../../entities/EventLog/EventLog'
import { Experience } from '../../entities/Experience/Experience'
import { FullLoketUser } from '../../entities/LoketUser/LoketUser'
import { Permission } from '../../entities/Permission/Permission'
import { getPersonFullName } from '../../entities/Person/PersonHelpers'
import { Role } from '../../entities/Role/Role'
import { TransportationOption } from '../../entities/TransportationOption/TransportationOption'
import { AuthenticatedUser, UserProfile } from '../../entities/User/User'
import { transformUserProfileV3ToFullUser } from '../../entities/User/UserTransformers'
import useCheckPermission from '../../helpers/hooks/useCheckPermission'
import trans from '../../helpers/trans'
import { UsersRoutes } from '../../routes/helpers'
import { PrivateRouteGroupRoutes } from '../../routes/routes'
import { UserStatus } from '../../types/userStatus'
import { DeactivatedUserBar } from './subcomponents'

import './UserDetail.scss'

interface UserDetailProps {
    isLoading: boolean;
    isLoadingLogs: boolean;
    isLoadingUserFormData: boolean;
    isLoadingLoketUser: boolean;
    isLogsModalOpen: boolean;
    currentUser: AuthenticatedUser;
    employmentTypes: EmploymentType[];
    experiences: Experience[];
    transportationOptions: TransportationOption[];
    logs: EventLog[];
    permissions: Permission[];
    roles: Role[];
    user: UserProfile;
    loketUser?: FullLoketUser;
    onAfterEditUserSuccess: () => void;
    onHistoryButtonClick: () => void;
    onLogsModalClose: () => void;
    onResendRegistrationMailButtonClick: () => void;
    onSendResetPasswordAndPinButtonClick: () => void;
    className?: string;
}

const UserDetail: FC<UserDetailProps> = ({
    isLoading,
    isLoadingLogs,
    isLoadingUserFormData,
    isLoadingLoketUser,
    isLogsModalOpen,
    currentUser,
    employmentTypes,
    experiences,
    transportationOptions,
    logs,
    permissions,
    roles,
    user,
    loketUser,
    onAfterEditUserSuccess,
    onHistoryButtonClick,
    onLogsModalClose,
    onResendRegistrationMailButtonClick,
    onSendResetPasswordAndPinButtonClick,
    className = '',
}): ReactElement => {
    const [isEditUserFormOpen, toggleIsEditUserFormOpen] = useToggle(false)
    const [isDeactivateUserFormOpen, toggleIsDeactivateUserFormOpen] = useToggle(false)
    const [isReactivateUserFormOpen, toggleIsReactivateUserFormOpen] = useToggle(false)
    const [isDeleteUserFormOpen, toggleIsDeleteUserFormOpen] = useToggle(false)

    const userFullName = getPersonFullName(user.person)
    const canEditUserStatus = useCheckPermission('edit-user-status')
    const canEditUser = hasPermissionToEditUser(user, permissions, currentUser.role, currentUser.id)
    const canViewLogs = useCheckPermission('view-all-logs')
    const canViewLoketData = useCheckPermission('manage-loket-data')
    const isSameUserAsCurrentUser = currentUser.id === user.id

    return (
        <div className={`user-detail ${className}`}>
            {user.status !== UserStatus.active && (
                <DeactivatedUserBar
                    showButtons={canEditUserStatus && ! isSameUserAsCurrentUser}
                    userFullName={userFullName}
                    userStatus={user.status}
                    onDeleteButtonClick={toggleIsDeleteUserFormOpen}
                    onReactivateButtonClick={toggleIsReactivateUserFormOpen}
                />
            )}

            <div className="user-detail__main">
                <div className="user-detail__main-top-bar">
                    <IconNavLink
                        icon="reset"
                        text={trans('common.back')}
                        to={`${PrivateRouteGroupRoutes.management()}/${UsersRoutes.users()}`}
                        className="user-detail__back-button"
                        iconClassName="user-detail__back-button-icon"
                    />

                    {canEditUser && (
                        <IconButton
                            icon="edit"
                            text={trans('common.edit')}
                            disabled={isLoadingUserFormData}
                            onClick={toggleIsEditUserFormOpen}
                            className="user-detail__edit-button"
                        />
                    )}
                </div>

                <UserDetailCards
                    isLoadingLoketUser={isLoadingLoketUser}
                    canViewLogs={canViewLogs}
                    canViewLoketData={canViewLoketData}
                    user={user}
                    loketUser={loketUser}
                    onDeactivateUserButtonClick={toggleIsDeactivateUserFormOpen}
                    onHistoryButtonClick={onHistoryButtonClick}
                    onResendRegistrationMailButtonClick={onResendRegistrationMailButtonClick}
                    onSendResetPasswordAndPinButtonClick={onSendResetPasswordAndPinButtonClick}
                    className="user-detail__cards"
                />

                <ConnectedLegacyUserDetail className="user-detail__legacy-user-detail" />
            </div>

            {isLoading && <LoadingSpinner className="user-detail__loader" />}

            {isEditUserFormOpen && (
                <PacoModal onClose={() => toggleIsEditUserFormOpen(false)}>
                    <ModalContent title={trans('common.editUser')}>
                        <ConnectedEditUserForm
                            experiences={experiences}
                            transportationOptions={transportationOptions}
                            roles={roles}
                            employmentTypes={employmentTypes}
                            fullUser={transformUserProfileV3ToFullUser(user)}
                            currentUserId={currentUser.id}
                            afterEditUserSuccess={onAfterEditUserSuccess}
                            onClose={toggleIsEditUserFormOpen}
                        />
                    </ModalContent>
                </PacoModal>
            )}

            {isDeactivateUserFormOpen && (
                <PacoModal onClose={() => toggleIsDeactivateUserFormOpen(false)}>
                    <ModalContent title={trans('common.deactivate')}>
                        <ConnectedDeactivateUserForm
                            user={user}
                            onClose={toggleIsDeactivateUserFormOpen}
                        />
                    </ModalContent>
                </PacoModal>
            )}

            {isReactivateUserFormOpen && (
                <PacoModal onClose={() => toggleIsReactivateUserFormOpen(false)}>
                    <ModalContent title={trans('common.reactivate')}>
                        <ConnectedReactivateUserForm
                            user={user}
                            onClose={toggleIsReactivateUserFormOpen}
                        />
                    </ModalContent>
                </PacoModal>
            )}

            {isDeleteUserFormOpen && (
                <PacoModal onClose={() => toggleIsDeleteUserFormOpen(false)}>
                    <ModalContent title={trans('common.delete')}>
                        <ConnectedDeleteUserForm
                            user={user}
                            onClose={toggleIsDeleteUserFormOpen}
                        />
                    </ModalContent>
                </PacoModal>
            )}

            <PacoLogsModal
                isLoading={isLoadingLogs}
                isOpen={isLogsModalOpen}
                title={trans('containers.userDetail.userLog')}
                emptyMessage={trans('containers.userDetail.emptyUserLog')}
                logs={logs}
                onClose={onLogsModalClose}
            />
        </div>
    )
}

export default UserDetail
